import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiVisitEngine } from '@/shared/api/client'
import { queryKeys } from '@/shared/api/query-keys'
import type { IApiResponse } from '@/shared/types/api'
import type {
  ICertificate,
  ICertificateContext,
  ICertificateVerifyResult,
  ICreateCertificatePayload,
  TCertificateLang,
} from '../model/certificate.types'

// ── Raw API ───────────────────────────────────────────────────────────────────

export const certificateApi = {
  getList: async (donorId: string) => {
    const response = await apiVisitEngine.get<IApiResponse<ICertificate[]>>(
      '/v1/certificates',
      { params: { donor_id: donorId } },
    )
    return response.data.data
  },

  getById: async (id: string) => {
    const response = await apiVisitEngine.get<IApiResponse<ICertificate>>(
      `/v1/certificates/${id}`,
    )
    return response.data.data
  },

  getContext: async (donorId: string) => {
    const response = await apiVisitEngine.get<IApiResponse<ICertificateContext>>(
      '/v1/certificates/context',
      { params: { donor_id: donorId } },
    )
    return response.data.data
  },

  create: async (payload: ICreateCertificatePayload) => {
    const response = await apiVisitEngine.post<IApiResponse<ICertificate>>(
      '/v1/certificates',
      payload,
    )
    return response.data.data
  },

  generate: async (id: string, lang: TCertificateLang) => {
    const response = await apiVisitEngine.post<IApiResponse<ICertificate>>(
      `/v1/certificates/${id}/generate`,
      undefined,
      { params: { lang } },
    )
    return response.data.data
  },

  download: async (id: string, lang: TCertificateLang) => {
    const response = await apiVisitEngine.get<Blob>(
      `/v1/certificates/${id}/download`,
      { params: { lang }, responseType: 'blob' },
    )
    return response.data
  },

  verify: async (verificationToken: string) => {
    const response = await apiVisitEngine.get<
      IApiResponse<ICertificateVerifyResult>
    >(`/public/v1/certificates/verify/${verificationToken}`)
    return response.data.data
  },
}

// ── Query hooks ───────────────────────────────────────────────────────────────

export const useGetCertificatesQuery = (donorId: string) =>
  useQuery({
    queryKey: queryKeys.certificate.list(donorId),
    queryFn: () => certificateApi.getList(donorId),
    enabled: !!donorId,
  })

export const useGetCertificateQuery = (id: string) =>
  useQuery({
    queryKey: queryKeys.certificate.detail(id),
    queryFn: () => certificateApi.getById(id),
    enabled: !!id,
  })

export const useGetCertificateContextQuery = (donorId: string) =>
  useQuery({
    queryKey: queryKeys.certificate.context(donorId),
    queryFn: () => certificateApi.getContext(donorId),
    enabled: !!donorId,
  })

// ── Mutation hooks ────────────────────────────────────────────────────────────

export const useCreateCertificateMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (payload: ICreateCertificatePayload) =>
      certificateApi.create(payload),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.certificate.list(variables.donorId),
      })
      queryClient.invalidateQueries({
        queryKey: queryKeys.certificate.context(variables.donorId),
      })
    },
  })
}

export const useGenerateCertificateMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, lang }: { id: string; lang: TCertificateLang }) =>
      certificateApi.generate(id, lang),
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.certificate.detail(data.id),
      })
      queryClient.invalidateQueries({
        queryKey: queryKeys.certificate.list(data.donor_id),
      })
    },
  })
}

export const useDownloadCertificateMutation = () =>
  useMutation({
    mutationFn: ({ id, lang }: { id: string; lang: TCertificateLang }) =>
      certificateApi.download(id, lang),
  })

export const useVerifyCertificateQuery = (verificationToken: string) =>
  useQuery({
    queryKey: queryKeys.certificate.verify(verificationToken),
    queryFn: () => certificateApi.verify(verificationToken),
    retry: false,
    enabled: !!verificationToken,
  })
